import type { MouseEvent, ReactElement, ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import type { JobAppNotification } from 'context/NotificationCenterContext';
import {
  getNotificationCompanyBodySuffix,
  getNotificationProcessingTypeLabel,
  type GlobalEventTheme,
} from './NotificationCenter.constants';
import {
  cardTitleSx,
  descriptionSx,
  getThemedIconCircleSx,
  jobCompanyNameSx,
  notificationRowGridSx,
  notificationSubtitleSx,
  processingTypeChipSx,
  startedTypeChipSx,
  tagsRowSx,
  unreadColumnSx,
  unreadStatusDotSx,
} from './NotificationCenter.styles';
import { NOTIFICATION_VARIANT, type NotificationVariant } from './NotificationCenter.types';

interface NotificationDismissButtonProps {
  onClick: (e: MouseEvent) => void;
}

export const NotificationDismissButton = ({ onClick }: NotificationDismissButtonProps) => (
  <IconButton
    type="button"
    size="small"
    aria-label="Dismiss notification"
    onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }}
    sx={{ p: 0.25, mt: 0.125 }}>
    <CloseIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
  </IconButton>
);

interface NotificationUnreadColumnProps {
  read: boolean;
}

export const NotificationUnreadColumn = ({ read }: NotificationUnreadColumnProps) => (
  <Box sx={unreadColumnSx} aria-hidden>
    {read ? null : <Box sx={unreadStatusDotSx} />}
  </Box>
);

interface ThemedNotificationBodyProps {
  theme: GlobalEventTheme;
  icon: ReactElement;
  title: string;
  timestamp?: ReactNode;
  children?: ReactNode;
  showUnreadColumn?: boolean;
  read?: boolean;
  onDismiss: (e: MouseEvent) => void;
}

/** Icon + title/timestamp + optional subtitle/CTA + optional unread + dismiss */
export const ThemedNotificationBody = ({
  theme,
  icon,
  title,
  timestamp,
  children,
  showUnreadColumn = false,
  read = false,
  onDismiss,
}: ThemedNotificationBodyProps) => (
  <Box sx={notificationRowGridSx(showUnreadColumn)}>
    <Box sx={getThemedIconCircleSx(theme.iconBg)}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography sx={{ ...cardTitleSx, color: theme.titleColor }}>{title}</Typography>
        {timestamp}
      </Box>
      {children}
    </Box>
    {showUnreadColumn && <NotificationUnreadColumn read={read} />}
    <NotificationDismissButton onClick={onDismiss} />
  </Box>
);

export const NotificationSubtitle = ({ children }: { children: ReactNode }) => (
  <Typography sx={notificationSubtitleSx}>{children}</Typography>
);

interface JobNotificationMainTextProps {
  variant: NotificationVariant;
  job: Pick<JobAppNotification['job'], 'companyName' | 'processingType' | 'errorMessage'>;
  title: string;
  titleColor: string;
  timestamp?: ReactNode;
  /** Card-only: expandable file list under started/processing */
  startedExtra?: ReactNode;
  /** Card/toast: result links or “View results” label under completed/failed */
  resultsExtra?: ReactNode;
}

export const JobNotificationMainText = ({
  variant,
  job,
  title,
  titleColor,
  timestamp,
  startedExtra,
  resultsExtra,
}: JobNotificationMainTextProps) => {
  const { STARTED, PROCESSING, COMPLETED, FAILED } = NOTIFICATION_VARIANT;

  return (
    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
        <Typography sx={{ ...cardTitleSx, color: titleColor }}>{title}</Typography>
        {timestamp}
      </Box>

      {(variant === STARTED || variant === PROCESSING) && (
        <>
          {job.companyName && (
            <Typography sx={jobCompanyNameSx}>{job.companyName}</Typography>
          )}
          {job.processingType && (
            <Box sx={{ mt: 0.75 }}>
              <Chip
                label={getNotificationProcessingTypeLabel(job.processingType)}
                size="small"
                sx={variant === STARTED ? startedTypeChipSx : processingTypeChipSx}
              />
            </Box>
          )}
          {startedExtra}
        </>
      )}

      {(variant === COMPLETED || variant === FAILED) && job.companyName && (
        <Typography sx={descriptionSx} mt={0.5}>
          {job.companyName}{' '}
          {getNotificationCompanyBodySuffix(variant)}
        </Typography>
      )}

      {variant === FAILED && job.errorMessage && (
        <Typography sx={{ ...descriptionSx, color: '#b91c1c', fontSize: '0.78rem' }} mt={0.25}>
          {job.errorMessage}
        </Typography>
      )}

      {(variant === COMPLETED || variant === FAILED) && (
        <>
          {job.processingType && (
            <Box sx={tagsRowSx}>
              <Chip
                label={getNotificationProcessingTypeLabel(job.processingType)}
                size="small"
                sx={processingTypeChipSx}
              />
            </Box>
          )}
          {resultsExtra}
        </>
      )}
    </Box>
  );
};
